import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  GridLegacy as Grid,
  Card,
  CardContent,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Message as MessageIcon,
  AttachMoney as MoneyIcon,
  Edit as EditIcon,
  Preview as PreviewIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { smsSettingsService } from '../../services/smsSettings.service';

interface SMSSetting {
  value: string;
  description: string;
  id: number;
}

interface SMSSettings {
  [key: string]: SMSSetting;
}

interface CostStats {
  date: string;
  message_type: string;
  message_count: number;
  total_cost: number;
  avg_cost: number;
  successful: number;
  failed: number;
}

const SMSSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<SMSSettings>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [costStats, setCostStats] = useState<CostStats[]>([]);
  const [totalCost, setTotalCost] = useState<any>(null);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewMessage, setPreviewMessage] = useState('');

  useEffect(() => {
    fetchSettings();
    fetchCostStats();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await smsSettingsService.getSettings();
      setSettings(response);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch SMS settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchCostStats = async () => {
    try {
      const [statsResponse, totalResponse] = await Promise.all([
        smsSettingsService.getCostStats(),
        smsSettingsService.getTotalCost()
      ]);
      setCostStats(statsResponse);
      setTotalCost(totalResponse);
    } catch (err: any) {

    }
  };

  const handleSettingChange = async (key: string, value: string) => {
    try {
      await smsSettingsService.updateSetting(key, value);
      setSettings(prev => ({
        ...prev,
        [key]: { ...prev[key], value }
      }));
      setSuccess('Setting updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update setting');
    }
  };

  const handleTemplateEdit = (templateType: string) => {
    const templateKey = `${templateType}_template`;
    const template = settings[templateKey]?.value || '';
    setSelectedTemplate(templateType);
    setTemplateContent(template);
    setTemplateDialog(true);
  };

  const handleTemplateSave = async () => {
    try {
      await smsSettingsService.updateTemplate(selectedTemplate, templateContent);
      setSettings(prev => ({
        ...prev,
        [`${selectedTemplate}_template`]: {
          ...prev[`${selectedTemplate}_template`],
          value: templateContent
        }
      }));
      setTemplateDialog(false);
      setSuccess('Template updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update template');
    }
  };

  const handleTemplatePreview = async () => {
    try {
      const sampleData = {
        customerName: 'John Doe',
        trackingNumber: 'K1234',
        branchName: 'Main Branch',
        status: 'Ready for Pickup',
        statusMessage: 'Your order is ready for pickup',
        amount: '$50.00',
        message: 'Custom message here'
      };

      const response = await smsSettingsService.testTemplate(selectedTemplate, templateContent, sampleData);
      setPreviewMessage(response.parsedMessage);
      setPreviewDialog(true);
    } catch (err: any) {
      setError(err.message || 'Failed to preview template');
    }
  };

  const getSettingValue = (key: string): boolean => {
    return settings[key]?.value === 'true';
  };

  const getTemplateValue = (templateType: string): string => {
    const key = `${templateType}_template`;
    return settings[key]?.value || '';
  };

  if (loading) {
    return <Typography>Loading SMS settings...</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        <MessageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        SMS Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Notification Settings" icon={<SettingsIcon />} />
        <Tab label="Message Templates" icon={<MessageIcon />} />
        <Tab label="Cost Analytics" icon={<MoneyIcon />} />
      </Tabs>

      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Enable/Disable Notifications
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={getSettingValue('sms_enabled')}
                      onChange={(e) => handleSettingChange('sms_enabled', e.target.checked.toString())}
                    />
                  }
                  label="Enable All SMS Notifications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={getSettingValue('pickup_notification_enabled')}
                      onChange={(e) => handleSettingChange('pickup_notification_enabled', e.target.checked.toString())}
                      disabled={!getSettingValue('sms_enabled')}
                    />
                  }
                  label="Pickup Notifications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={getSettingValue('status_update_enabled')}
                      onChange={(e) => handleSettingChange('status_update_enabled', e.target.checked.toString())}
                      disabled={!getSettingValue('sms_enabled')}
                    />
                  }
                  label="Status Update Notifications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={getSettingValue('payment_reminder_enabled')}
                      onChange={(e) => handleSettingChange('payment_reminder_enabled', e.target.checked.toString())}
                      disabled={!getSettingValue('sms_enabled')}
                    />
                  }
                  label="Payment Reminder Notifications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={getSettingValue('custom_message_enabled')}
                      onChange={(e) => handleSettingChange('custom_message_enabled', e.target.checked.toString())}
                      disabled={!getSettingValue('sms_enabled')}
                    />
                  }
                  label="Custom Message Notifications"
                />
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Cost Summary
              </Typography>
              {totalCost && (
                <Box>
                  <Typography variant="h4" color="primary">
                    ${totalCost.total_cost?.toFixed(4) || '0.0000'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total SMS Cost
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="h6">{totalCost.total_messages || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">Total Messages</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="h6" color="success.main">
                        {totalCost.successful_cost?.toFixed(4) || '0.0000'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Successful Cost</Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Message Templates
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Customize your SMS message templates. Use variables like {'{customerName}'}, {'{trackingNumber}'}, {'{branchName}'}, etc.
              </Typography>

              {['pickup_notification', 'status_update', 'payment_reminder', 'custom_message'].map((templateType) => (
                <Card key={templateType} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
                        {templateType.replace(/_/g, ' ')}
                      </Typography>
                      <Box>
                        <Tooltip title="Edit Template">
                          <IconButton onClick={() => handleTemplateEdit(templateType)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      fontFamily: 'monospace', 
                      bgcolor: 'grey.100', 
                      p: 1, 
                      borderRadius: 1,
                      whiteSpace: 'pre-wrap'
                    }}>
                      {getTemplateValue(templateType)}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Paper>
          </Grid>
        </Grid>
      )}

      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                SMS Cost Analytics
              </Typography>
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Message Type</TableCell>
                      <TableCell>Messages Sent</TableCell>
                      <TableCell>Successful</TableCell>
                      <TableCell>Failed</TableCell>
                      <TableCell>Total Cost</TableCell>
                      <TableCell>Avg Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {costStats.map((stat, index) => (
                      <TableRow key={index}>
                        <TableCell>{new Date(stat.date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Chip 
                            label={stat.message_type.replace(/_/g, ' ')} 
                            size="small" 
                            color="primary" 
                          />
                        </TableCell>
                        <TableCell>{stat.message_count}</TableCell>
                        <TableCell>
                          <Chip 
                            label={stat.successful} 
                            size="small" 
                            color="success" 
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={stat.failed} 
                            size="small" 
                            color="error" 
                          />
                        </TableCell>
                        <TableCell>${stat.total_cost?.toFixed(4) || '0.0000'}</TableCell>
                        <TableCell>${stat.avg_cost?.toFixed(4) || '0.0000'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Template Edit Dialog */}
      <Dialog open={templateDialog} onClose={() => setTemplateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit Template: {selectedTemplate.replace(/_/g, ' ')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Available variables: {'{customerName}'}, {'{trackingNumber}'}, {'{branchName}'}, {'{status}'}, {'{amount}'}, {'{message}'}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={templateContent}
            onChange={(e) => setTemplateContent(e.target.value)}
            placeholder="Enter your template here..."
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialog(false)}>Cancel</Button>
          <Button onClick={handleTemplatePreview} startIcon={<PreviewIcon />}>
            Preview
          </Button>
          <Button onClick={handleTemplateSave} variant="contained" startIcon={<SaveIcon />}>
            Save Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Template Preview</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ 
            bgcolor: 'grey.100', 
            p: 2, 
            borderRadius: 1,
            whiteSpace: 'pre-wrap'
          }}>
            {previewMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SMSSettingsPage; 